import React, { useEffect, useState } from 'react'
import { supabase } from './supabaseClient'
import {
  Box,
  CircularProgress,
  Typography,
  Paper,
  Grid,
  Button,
  Chip,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  Divider,
  Stack,
  useMediaQuery,
  InputBase,
  IconButton,
  alpha,
  useTheme,
  Container,
  Badge,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import {
  Favorite as FavoriteIcon,
  CalendarToday as CalendarTodayIcon,
  Done as DoneIcon,
  Close as CloseIcon,
  Info as InfoIcon,
  ArrowForward as ArrowForwardIcon,
  Search as SearchIcon,
  Assignment as AssignmentIcon,
  Home as HomeIcon,
  Add as AddIcon,
  EuroSymbol as EuroSymbolIcon,
  FilterList as FilterListIcon,
  MoreVert as MoreVertIcon,
  FlagOutlined as FlagOutlinedIcon,
  AccessTimeOutlined as AccessTimeOutlinedIcon,
  AllInboxOutlined as AllInboxOutlinedIcon,
} from '@mui/icons-material'
import { motion } from 'framer-motion'
// import NoResultsImg from './assets/no-results.svg' // You'll need to add this asset

// Helper function to format dates in a more readable way
const formatDate = (dateString) => {
  if (!dateString) return 'N/A'

  const date = new Date(dateString)
  const now = new Date()
  const yesterday = new Date(now)
  yesterday.setDate(now.getDate() - 1)

  // Today
  if (date.toDateString() === now.toDateString()) {
    return `Today at ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
  }

  // Yesterday
  if (date.toDateString() === yesterday.toDateString()) {
    return `Yesterday at ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
  }

  // Show full date for older dates
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

// Status chip based on status value
const StatusChip = ({ status, type }) => {
  const statusColors = {
    // Viewing statuses
    pending: { color: 'warning', icon: <InfoIcon fontSize="small" />, label: 'Pending' },
    requested: { color: 'info', icon: <InfoIcon fontSize="small" />, label: 'Requested' },
    accepted: { color: 'success', icon: <DoneIcon fontSize="small" />, label: 'Accepted' },
    confirmed: { color: 'success', icon: <DoneIcon fontSize="small" />, label: 'Confirmed' },
    completed: { color: 'primary', icon: <DoneIcon fontSize="small" />, label: 'Completed' },
    declined: { color: 'error', icon: <CloseIcon fontSize="small" />, label: 'Declined' },
    canceled: { color: 'default', icon: <CloseIcon fontSize="small" />, label: 'Canceled' },
    revoked: { color: 'default', icon: <CloseIcon fontSize="small" />, label: 'Revoked' },

    // Offer statuses
    accepted_offer: { color: 'success', icon: <DoneIcon fontSize="small" />, label: 'Accepted' },
    rejected_offer: { color: 'error', icon: <CloseIcon fontSize="small" />, label: 'Rejected' },
    pending_offer: { color: 'warning', icon: <InfoIcon fontSize="small" />, label: 'Pending' },
  }

  const statusInfo = statusColors[status] ||
    statusColors[`${status}_${type}`] || {
      color: 'default',
      icon: <InfoIcon fontSize="small" />,
      label: status,
    }

  return (
    <Chip
      icon={statusInfo.icon}
      label={statusInfo.label}
      color={statusInfo.color}
      size="small"
      variant="filled"
      sx={{
        textTransform: 'capitalize',
        fontWeight: 500,
      }}
    />
  )
}

// Property listing card
const PropertyCard = ({ listing, onViewDetails }) => {
  const theme = useTheme()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const handleViewDetails = () => {
    navigate(`/listing/${listing.id}`)
  }

  // Function to determine icon based on engagement type
  const getEngagementIcon = () => {
    if (listing.viewing_status) {
      return <CalendarTodayIcon fontSize="small" />
    } else if (listing.offer_status) {
      return <EuroSymbolIcon fontSize="small" />
    } else if (listing.liked) {
      return <FavoriteIcon fontSize="small" color="error" />
    }
    return null
  }

  // Function to determine label based on engagement type
  const getEngagementLabel = () => {
    if (listing.viewing_status) {
      if (listing.viewing_date) {
        if (new Date(listing.viewing_date) < new Date()) {
          return t('listings.viewingHappenedOn')
        } else {
          return t('listings.viewingScheduledFor')
        }
      }
      return 'Viewing'
    } else if (listing.offer_status) {
      return 'Offer'
    } else if (listing.liked) {
      return 'Liked'
    }
    return ''
  }

  // Helper to check if a property is in progress (active viewing or offer)
  const isInProgress = () => {
    const activeViewingStatuses = ['pending', 'accepted', 'confirmed', 'requested', 'proposed']
    const activeOfferStatuses = ['pending']

    return (
      (listing.viewing_status && activeViewingStatuses.includes(listing.viewing_status)) ||
      (listing.offer_status && activeOfferStatuses.includes(listing.offer_status))
    )
  }

  // Check if this is a priority (hot) listing
  const isPriority = () => {
    return (
      isInProgress() ||
      (listing.created_at &&
        new Date(listing.created_at) > new Date(Date.now() - 7 * 24 * 60 * 60 * 1000))
    )
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <Card
        elevation={2}
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 2,
          transition: 'all 0.3s ease',
          overflow: 'hidden',
          position: 'relative',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: theme.shadows[8],
          },
          border: isPriority() ? `2px solid ${theme.palette.primary.main}` : 'none',
        }}
      >
        {/* Sold Overlay */}
        {listing.sold_at && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              bgcolor: 'rgba(0, 0, 0, 0.6)',
              zIndex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography
              variant="h5"
              component="div"
              sx={{
                color: 'white',
                fontWeight: 'bold',
                transform: 'rotate(-15deg)',
                border: '3px solid white',
                px: 2,
                py: 1,
                borderRadius: 1,
              }}
            >
              {t('listings.sold', 'SOLD')}
            </Typography>
          </Box>
        )}

        {/* Property Image */}
        <Box sx={{ position: 'relative' }}>
          <CardMedia
            component="img"
            height="180"
            image={listing.image_url || '/api/placeholder/400/300'}
            alt={listing.name}
            sx={{
              objectFit: 'cover',
              transition: 'transform 0.5s ease',
              '&:hover': {
                transform: 'scale(1.05)',
              },
            }}
          />

          {/* Price Badge */}
          <Box
            sx={{
              position: 'absolute',
              bottom: 0,
              right: 0,
              bgcolor: 'primary.dark',
              color: 'white',
              px: 2,
              py: 0.5,
              borderTopLeftRadius: 8,
              fontWeight: 'bold',
              boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
            }}
          >
            €{listing.price ? listing.price.toLocaleString() : 'N/A'}
          </Box>

          {/* Status Indicator (top left) */}
          {(listing.viewing_status || listing.offer_status) && (
            <Box
              sx={{
                position: 'absolute',
                top: 10,
                left: 10,
                zIndex: 2,
              }}
            >
              {listing.viewing_status && (
                <StatusChip status={listing.viewing_status} type="viewing" />
              )}
              {listing.offer_status && !listing.viewing_status && (
                <StatusChip status={listing.offer_status} type="offer" />
              )}
            </Box>
          )}

          {/* New badge */}
          {listing.created_at &&
            new Date(listing.created_at) > new Date(Date.now() - 7 * 24 * 60 * 60 * 1000) &&
            !listing.viewing_status &&
            !listing.offer_status && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 10,
                  left: 10,
                  zIndex: 2,
                  bgcolor: theme.palette.info.main,
                  color: 'white',
                  fontSize: '0.75rem',
                  fontWeight: 'bold',
                  px: 1,
                  py: 0.5,
                  borderRadius: 1,
                  boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                }}
              >
                NEW
              </Box>
            )}
        </Box>

        {/* Content */}
        <CardContent sx={{ flexGrow: 1, pb: 1 }}>
          <Typography
            variant="h6"
            component="div"
            gutterBottom
            noWrap
            title={listing.name}
            sx={{ fontWeight: 'bold' }}
          >
            {listing.name}
          </Typography>

          <Typography
            variant="body2"
            color="text.secondary"
            paragraph
            sx={{
              mb: 1,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {listing.address || `${listing.postcode}, ${listing.city}`}
          </Typography>

          {/* Property Features */}
          <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 0.5 }}>
            {listing.bedrooms && (
              <Chip
                label={`${listing.bedrooms} ${listing.bedrooms === 1 ? 'bed' : 'beds'}`}
                size="small"
                color="primary"
                variant="outlined"
                sx={{ fontWeight: 500 }}
              />
            )}

            {listing.bathrooms && (
              <Chip
                label={`${listing.bathrooms} ${listing.bathrooms === 1 ? 'bath' : 'baths'}`}
                size="small"
                color="primary"
                variant="outlined"
                sx={{ fontWeight: 500 }}
              />
            )}

            {listing.surface && (
              <Chip
                label={`${listing.surface} m²`}
                size="small"
                color="primary"
                variant="outlined"
                sx={{ fontWeight: 500 }}
              />
            )}
          </Stack>

          {/* Engagement Info (Viewing/Offer details) */}
          {(listing.viewing_status || listing.offer_status || listing.liked) && (
            <Box sx={{ mt: 'auto' }}>
              <Divider sx={{ mb: 1.5 }} />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {getEngagementIcon()}
                <Typography variant="body2" color="text.secondary">
                  {getEngagementLabel()}
                  {listing.viewing_date && `: ${formatDate(listing.viewing_date)}`}
                  {listing.offer_price && `: €${listing.offer_price.toLocaleString()}`}
                </Typography>
              </Box>
            </Box>
          )}
        </CardContent>

        {/* Actions */}
        <CardActions sx={{ justifyContent: 'flex-end', pt: 0 }}>
          <Button
            endIcon={<ArrowForwardIcon />}
            onClick={handleViewDetails}
            sx={{
              fontWeight: 500,
              borderRadius: 4,
              px: 2,
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.08),
              },
            }}
          >
            {t('listings.seeDetails')}
          </Button>
        </CardActions>
      </Card>
    </motion.div>
  )
}

// Filter category button component
const FilterButton = ({ icon, label, count, active, onClick }) => {
  const theme = useTheme()

  return (
    <Button
      onClick={onClick}
      startIcon={icon}
      variant={active ? 'contained' : 'outlined'}
      sx={{
        borderRadius: 8,
        px: 2,
        py: 1,
        mb: { xs: 1, md: 0 },
        mr: { xs: 0, md: 2 },
        backgroundColor: active ? 'primary.main' : 'background.paper',
        borderColor: active ? 'primary.main' : alpha(theme.palette.text.primary, 0.12),
        '&:hover': {
          backgroundColor: active ? 'primary.dark' : alpha(theme.palette.primary.main, 0.08),
          borderColor: active ? 'primary.dark' : 'primary.main',
        },
        minWidth: { xs: '100%', md: 'auto' },
        justifyContent: 'flex-start',
      }}
    >
      {label}
      {count > 0 && (
        <Chip
          label={count}
          size="small"
          color={active ? 'default' : 'primary'}
          sx={{
            ml: 1,
            height: 20,
            backgroundColor: active ? 'rgba(255,255,255,0.3)' : undefined,
            color: active ? 'white' : undefined,
            '& .MuiChip-label': { px: 1, py: 0.25 },
            fontWeight: 'bold',
          }}
        />
      )}
    </Button>
  )
}

export default function ManageListings() {
  const { t } = useTranslation()
  const theme = useTheme()
  const navigate = useNavigate()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const isTablet = useMediaQuery(theme.breakpoints.down('md'))

  const [listings, setListings] = useState([])
  const [loading, setLoading] = useState(true)
  const [user, setUser] = useState(null)
  const [activeFilter, setActiveFilter] = useState('all')
  const [searchTerm, setSearchTerm] = useState('')
  const [filterMenuAnchor, setFilterMenuAnchor] = useState(null)

  // Get current date
  const currentDate = new Date()
  const oneWeekAgo = new Date()
  oneWeekAgo.setDate(currentDate.getDate() - 7)

  // Fetch user data
  useEffect(() => {
    async function fetchUser() {
      const { data: userData } = await supabase.auth.getUser()
      if (userData) setUser(userData.user)
    }
    fetchUser()
  }, [])

  // Fetch listings
  useEffect(() => {
    async function fetchListings() {
      if (!user) return

      setLoading(true)

      try {
        // Use the user_listed_properties view to get all user interactions
        const { data: userListings, error } = await supabase
          .from('user_listed_properties')
          .select('*')
        if (error) throw error

        setListings(userListings || [])
      } catch (error) {
        console.error('Error fetching listings:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchListings()
  }, [user])

  // Calculate counts for each category
  const getCounts = () => {
    // Active properties (viewing or offer in progress)
    const activeCount = listings.filter((listing) => {
      const activeViewingStatuses = ['pending', 'accepted', 'confirmed', 'requested', 'proposed']
      const activeOfferStatuses = ['pending']

      return (
        (listing.viewing_status && activeViewingStatuses.includes(listing.viewing_status)) ||
        (listing.offer_status && activeOfferStatuses.includes(listing.offer_status))
      )
    }).length

    // New properties from the last week
    const newCount = listings.filter((listing) => {
      const createdAt = new Date(listing.created_at)
      return createdAt > oneWeekAgo
    }).length

    // Favorited properties
    const favoritesCount = listings.filter((listing) => listing.liked === true).length

    return {
      all: listings.length,
      active: activeCount,
      new: newCount,
      favorites: favoritesCount,
    }
  }

  const counts = getCounts()

  // Filter listings based on filter and search term
  const getFilteredListings = () => {
    // Apply search filter first
    let filtered = listings

    if (searchTerm) {
      const search = searchTerm.toLowerCase()
      filtered = filtered.filter(
        (listing) =>
          (listing.name && listing.name.toLowerCase().includes(search)) ||
          (listing.city && listing.city.toLowerCase().includes(search)) ||
          (listing.postcode && listing.postcode.toLowerCase().includes(search)) ||
          (listing.address && listing.address.toLowerCase().includes(search))
      )
    }

    // Then apply category filter
    switch (activeFilter) {
      case 'active':
        return filtered.filter((listing) => {
          const activeViewingStatuses = [
            'pending',
            'accepted',
            'confirmed',
            'requested',
            'proposed',
          ]
          const activeOfferStatuses = ['pending']

          return (
            (listing.viewing_status && activeViewingStatuses.includes(listing.viewing_status)) ||
            (listing.offer_status && activeOfferStatuses.includes(listing.offer_status))
          )
        })

      case 'new':
        return filtered.filter((listing) => {
          const createdAt = new Date(listing.created_at)
          return createdAt > oneWeekAgo
        })

      case 'favorites':
        return filtered.filter((listing) => listing.liked === true)

      case 'all':
      default:
        return filtered
    }
  }

  const filteredListings = getFilteredListings()

  const handleFilterChange = (filter) => {
    setActiveFilter(filter)
    setFilterMenuAnchor(null)
  }

  const openFilterMenu = (event) => {
    setFilterMenuAnchor(event.currentTarget)
  }

  const closeFilterMenu = () => {
    setFilterMenuAnchor(null)
  }

  // Sort listings by priority
  const sortedListings = [...filteredListings].sort((a, b) => {
    // Active viewings/offers first
    const activeViewingStatuses = ['pending', 'accepted', 'confirmed', 'requested', 'proposed']
    const activeOfferStatuses = ['pending']

    const aIsActive =
      (a.viewing_status && activeViewingStatuses.includes(a.viewing_status)) ||
      (a.offer_status && activeOfferStatuses.includes(a.offer_status))

    const bIsActive =
      (b.viewing_status && activeViewingStatuses.includes(b.viewing_status)) ||
      (b.offer_status && activeOfferStatuses.includes(b.offer_status))

    if (aIsActive && !bIsActive) return -1
    if (!aIsActive && bIsActive) return 1

    // Then new listings
    const aIsNew = a.created_at && new Date(a.created_at) > oneWeekAgo
    const bIsNew = b.created_at && new Date(b.created_at) > oneWeekAgo

    if (aIsNew && !bIsNew) return -1
    if (!aIsNew && bIsNew) return 1

    // Then favorites
    if (a.liked && !b.liked) return -1
    if (!a.liked && b.liked) return 1

    // Then by created date (newest first)
    return new Date(b.created_at) - new Date(a.created_at)
  })

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Container maxWidth="xl" sx={{ pb: 8 }}>
      <Box sx={{ pt: 4, pb: 6 }}>
        {/* Header Section */}
        <Box
          sx={{
            textAlign: 'center',
            mb: 6,
          }}
        >
          <Typography
            variant="h3"
            component="h1"
            gutterBottom
            sx={{
              fontWeight: 700,
              background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              mb: 2,
            }}
          >
            Your Property Journey
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 800, mx: 'auto', mb: 3 }}>
            Track all your property interactions in one place
          </Typography>
        </Box>

        {/* Filter Controls & Search */}
        <Grid container spacing={2} sx={{ mb: 4 }}>
          <Grid item xs={12} md={isTablet ? 12 : 8}>
            {/* Filter buttons */}
            {!isTablet ? (
              <Stack direction="row" sx={{ flexWrap: 'wrap' }}>
                <FilterButton
                  icon={<AllInboxOutlinedIcon />}
                  label="All Properties"
                  count={counts.all}
                  active={activeFilter === 'all'}
                  onClick={() => handleFilterChange('all')}
                />
                <FilterButton
                  icon={<AccessTimeOutlinedIcon />}
                  label="Active"
                  count={counts.active}
                  active={activeFilter === 'active'}
                  onClick={() => handleFilterChange('active')}
                />
                <FilterButton
                  icon={<FlagOutlinedIcon />}
                  label="New"
                  count={counts.new}
                  active={activeFilter === 'new'}
                  onClick={() => handleFilterChange('new')}
                />
                <FilterButton
                  icon={<FavoriteIcon />}
                  label="Favorites"
                  count={counts.favorites}
                  active={activeFilter === 'favorites'}
                  onClick={() => handleFilterChange('favorites')}
                />
              </Stack>
            ) : (
              <>
                <Button
                  variant="outlined"
                  onClick={openFilterMenu}
                  startIcon={<FilterListIcon />}
                  endIcon={
                    <Badge badgeContent={activeFilter !== 'all' ? 1 : 0} color="primary">
                      <MoreVertIcon />
                    </Badge>
                  }
                  sx={{
                    width: '100%',
                    justifyContent: 'space-between',
                    borderRadius: 2,
                    py: 1.5,
                    mb: 2,
                    fontSize: '1rem',
                  }}
                >
                  {activeFilter === 'all' && 'All Properties'}
                  {activeFilter === 'active' && 'Active Properties'}
                  {activeFilter === 'new' && 'New Properties'}
                  {activeFilter === 'favorites' && 'Favorite Properties'}
                </Button>

                <Menu
                  anchorEl={filterMenuAnchor}
                  open={Boolean(filterMenuAnchor)}
                  onClose={closeFilterMenu}
                  PaperProps={{
                    sx: {
                      width: 240,
                      mt: 1,
                      borderRadius: 2,
                      boxShadow: theme.shadows[4],
                    },
                  }}
                >
                  <MenuItem
                    onClick={() => handleFilterChange('all')}
                    selected={activeFilter === 'all'}
                    sx={{ py: 1.5 }}
                  >
                    <ListItemIcon>
                      <AllInboxOutlinedIcon
                        color={activeFilter === 'all' ? 'primary' : 'inherit'}
                      />
                    </ListItemIcon>
                    <ListItemText>All Properties</ListItemText>
                    <Chip
                      label={counts.all}
                      size="small"
                      color="primary"
                      variant={activeFilter === 'all' ? 'filled' : 'outlined'}
                    />
                  </MenuItem>
                  <MenuItem
                    onClick={() => handleFilterChange('active')}
                    selected={activeFilter === 'active'}
                    sx={{ py: 1.5 }}
                  >
                    <ListItemIcon>
                      <AccessTimeOutlinedIcon
                        color={activeFilter === 'active' ? 'primary' : 'inherit'}
                      />
                    </ListItemIcon>
                    <ListItemText>Active</ListItemText>
                    <Chip
                      label={counts.active}
                      size="small"
                      color="primary"
                      variant={activeFilter === 'active' ? 'filled' : 'outlined'}
                    />
                  </MenuItem>
                  <MenuItem
                    onClick={() => handleFilterChange('new')}
                    selected={activeFilter === 'new'}
                    sx={{ py: 1.5 }}
                  >
                    <ListItemIcon>
                      <FlagOutlinedIcon color={activeFilter === 'new' ? 'primary' : 'inherit'} />
                    </ListItemIcon>
                    <ListItemText>New</ListItemText>
                    <Chip
                      label={counts.new}
                      size="small"
                      color="primary"
                      variant={activeFilter === 'new' ? 'filled' : 'outlined'}
                    />
                  </MenuItem>
                  <MenuItem
                    onClick={() => handleFilterChange('favorites')}
                    selected={activeFilter === 'favorites'}
                    sx={{ py: 1.5 }}
                  >
                    <ListItemIcon>
                      <FavoriteIcon color={activeFilter === 'favorites' ? 'primary' : 'inherit'} />
                    </ListItemIcon>
                    <ListItemText>Favorites</ListItemText>
                    <Chip
                      label={counts.favorites}
                      size="small"
                      color="primary"
                      variant={activeFilter === 'favorites' ? 'filled' : 'outlined'}
                    />
                  </MenuItem>
                </Menu>
              </>
            )}
          </Grid>

          <Grid item xs={12} md={isTablet ? 12 : 4}>
            {/* Search Box */}
            <Paper
              elevation={0}
              sx={{
                display: 'flex',
                alignItems: 'center',
                px: 2,
                py: 1,
                borderRadius: 8,
                border: `1px solid ${alpha(theme.palette.text.primary, 0.1)}`,
                bgcolor: alpha(theme.palette.background.paper, 0.8),
                '&:hover': {
                  borderColor: theme.palette.primary.main,
                },
                transition: 'all 0.3s ease',
                position: 'relative',
                mb: { xs: 2, md: 0 },
              }}
            >
              <SearchIcon color="action" sx={{ mr: 1 }} />
              <InputBase
                placeholder="Search by name, city, address..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                sx={{
                  ml: 1,
                  flex: 1,
                  '& .MuiInputBase-input': {
                    py: 1.25,
                  },
                }}
              />
              {searchTerm && (
                <IconButton edge="end" onClick={() => setSearchTerm('')} size="small">
                  <CloseIcon fontSize="small" />
                </IconButton>
              )}
            </Paper>
          </Grid>
        </Grid>

        {/* Results counter with Add Property Button */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 3,
          }}
        >
          <Typography variant="body1" color="text.secondary">
            {filteredListings.length === 0
              ? 'No properties found'
              : `${filteredListings.length} ${
                  filteredListings.length === 1 ? 'property' : 'properties'
                } found`}
          </Typography>

          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/discover')}
            sx={{
              borderRadius: 4,
              px: 3,
              py: 1,
            }}
          >
            Discover Properties
          </Button>
        </Box>

        {/* Listings Grid */}
        {sortedListings.length === 0 ? (
          <Box
            sx={{
              textAlign: 'center',
              py: 10,
              px: 2,
              bgcolor: alpha(theme.palette.background.paper, 0.5),
              borderRadius: 4,
              border: '1px dashed',
              borderColor: alpha(theme.palette.divider, 0.6),
              mt: 4,
            }}
          >
            <img
              src={'/assets/no-results.svg'}
              alt="No properties found"
              style={{
                maxWidth: '100%',
                height: 'auto',
                maxHeight: 200,
                marginBottom: 24,
                opacity: 0.7,
              }}
            />

            <Typography variant="h5" gutterBottom color="text.secondary" sx={{ fontWeight: 600 }}>
              {activeFilter === 'all' && searchTerm
                ? 'No matching properties found'
                : activeFilter === 'active'
                ? 'No active properties'
                : activeFilter === 'new'
                ? 'No new properties in the last week'
                : activeFilter === 'favorites'
                ? 'No favorite properties yet'
                : 'No properties found'}
            </Typography>

            <Typography
              variant="body1"
              color="text.secondary"
              paragraph
              sx={{ maxWidth: 500, mx: 'auto', mb: 4 }}
            >
              {activeFilter === 'all' && searchTerm
                ? 'Try adjusting your search terms or clear the search to see all your properties.'
                : activeFilter === 'active'
                ? 'Schedule a viewing or make an offer to see active properties here.'
                : activeFilter === 'new'
                ? 'Check back soon for new properties added in the last 7 days.'
                : activeFilter === 'favorites'
                ? 'Mark properties as favorites to save them here for later.'
                : 'Your property journey is about to begin!'}
            </Typography>

            <Button
              variant="contained"
              onClick={() => navigate('/discover')}
              startIcon={<SearchIcon />}
              sx={{ borderRadius: 8, px: 4, py: 1.5 }}
            >
              Discover Properties
            </Button>
          </Box>
        ) : (
          <Grid container spacing={3}>
            {sortedListings.map((listing) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={listing.id}>
                <PropertyCard
                  listing={listing}
                  onViewDetails={() => navigate(`/listing/${listing.id}`)}
                />
              </Grid>
            ))}
          </Grid>
        )}

        {/* Empty State - No Interactions at all */}
        {listings.length === 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <Paper
              elevation={0}
              sx={{
                mt: 8,
                textAlign: 'center',
                py: 8,
                px: { xs: 3, md: 6 },
                bgcolor: alpha(theme.palette.primary.light, 0.05),
                borderRadius: 4,
                border: `1px dashed ${alpha(theme.palette.primary.main, 0.3)}`,
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              {/* Decorative elements */}
              <Box
                sx={{
                  position: 'absolute',
                  top: -40,
                  right: -40,
                  width: 200,
                  height: 200,
                  borderRadius: '50%',
                  background: `radial-gradient(circle, ${alpha(
                    theme.palette.primary.main,
                    0.1
                  )} 0%, transparent 70%)`,
                  zIndex: 0,
                }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  bottom: -50,
                  left: -50,
                  width: 250,
                  height: 250,
                  borderRadius: '50%',
                  background: `radial-gradient(circle, ${alpha(
                    theme.palette.primary.light,
                    0.12
                  )} 0%, transparent 70%)`,
                  zIndex: 0,
                }}
              />

              <Box sx={{ position: 'relative', zIndex: 1 }}>
                <HomeIcon
                  sx={{
                    fontSize: 80,
                    color: 'primary.main',
                    opacity: 0.8,
                    mb: 3,
                    background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                    borderRadius: '50%',
                    p: 2,
                    color: 'white',
                    boxShadow: `0 12px 20px ${alpha(theme.palette.primary.main, 0.3)}`,
                  }}
                />

                <Typography variant="h4" gutterBottom sx={{ fontWeight: 700 }}>
                  {t('listings.noEngagements', 'Start Your Property Journey')}
                </Typography>

                <Typography
                  variant="body1"
                  paragraph
                  sx={{
                    mb: 4,
                    maxWidth: 700,
                    mx: 'auto',
                    color: alpha(theme.palette.text.primary, 0.7),
                  }}
                >
                  {t(
                    'listings.noEngagementsDesc',
                    'Discover properties, schedule viewings, and make offers. Your interactions will appear here to help you track your property search journey.'
                  )}
                </Typography>

                <Button
                  variant="contained"
                  onClick={() => navigate('/discover')}
                  startIcon={<SearchIcon />}
                  size="large"
                  sx={{
                    px: 4,
                    py: 1.5,
                    borderRadius: 8,
                    boxShadow: `0 8px 16px ${alpha(theme.palette.primary.main, 0.3)}`,
                  }}
                >
                  Start Discovering
                </Button>
              </Box>
            </Paper>
          </motion.div>
        )}
      </Box>
    </Container>
  )
}
